import { useTranslation } from "react-i18next";

import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Alert, Box, Stack, TextField, Typography } from "@mui/material";

import { FieldInfo } from "../../../asset-inventory/views/personal-data/components/field-info/FieldInfo";
import { ChecklistAnswer } from "../../BankruptcyChecklist.types";
import { FormattedText } from "../formatted-text/FormattedText";
import { YesNoToggle } from "../yes-no-toggle/YesNoToggle";

interface WarningConfig {
  active: boolean;
  text: string;
}

interface AnswerLabels {
  yes?: string;
  no?: string;
}

interface RemarkConfig {
  label?: string;
  value?: string;
  onChange: (value: string) => void;
}

interface Props {
  question: string;
  value: ChecklistAnswer;
  onChange: (value: ChecklistAnswer) => void;
  warning?: WarningConfig;
  answerLabels?: AnswerLabels;
  remark?: RemarkConfig;
  info?: string;
  conditionalInfo?: string;
}

export function ChecklistQuestion({
  question,
  value,
  onChange,
  warning,
  answerLabels,
  remark,
  info,
  conditionalInfo,
}: Props) {
  const { t } = useTranslation();

  const isAnswered = value !== null;
  const isWarning = warning?.active ?? false;
  const showWarningIcon = !isAnswered || isWarning;

  return (
    <Stack gap={1}>
      <Box display="flex" gap={1} alignItems="center">
        {showWarningIcon ? (
          <WarningAmberIcon
            fontSize="small"
            color="warning"
            titleAccess={t("clients.client.debtSettlement.bankruptcyChecklist.statusWarning")}
          />
        ) : (
          <CheckCircleOutlineIcon
            fontSize="small"
            color="success"
            titleAccess={t("clients.client.debtSettlement.bankruptcyChecklist.statusComplete")}
          />
        )}
        <Typography>{question}</Typography>
        {info && (
          <FieldInfo fieldLabel={question} dense>
            <FormattedText text={info} />
          </FieldInfo>
        )}
      </Box>

      <Stack gap={1} sx={{ pl: 3.5 }}>
        <Box display="flex" gap={2} alignItems="center">
          <YesNoToggle
            value={value}
            onChange={onChange}
            ariaLabel={question}
            yesLabel={answerLabels?.yes}
            noLabel={answerLabels?.no}
          />
          {!isAnswered && (
            <Typography variant="body2" color="warning.main">
              {t("clients.client.debtSettlement.bankruptcyChecklist.antwortFehlt")}
            </Typography>
          )}
        </Box>

        {isWarning && warning && (
          <Alert severity="warning">
            <FormattedText text={warning.text} />
          </Alert>
        )}

        {remark && (
          <TextField
            label={remark.label}
            value={remark.value ?? ""}
            onChange={(event) => remark.onChange(event.target.value)}
            multiline
            minRows={2}
            fullWidth
            size="small"
            sx={{ mt: 1 }}
          />
        )}

        {conditionalInfo && (
          <Alert severity="info">
            <FormattedText text={conditionalInfo} />
          </Alert>
        )}
      </Stack>
    </Stack>
  );
}
